import type { ExpressCheckoutButtonsState } from '@wix/ecom-platform-express-checkout-buttons';
import type { ExpressCheckoutMode } from '../expressCheckout/expressCheckoutEligibility';
import type { SelectedProduct, V3Product } from '@wix/bookings-products-picker';

export type { SelectedProduct, V3Product };
import { ExtendedBooking } from '@wix/ambassador-bookings-reader-v2-extended-booking/types';
import { CompletionRequirement } from '@wix/ambassador-bookings-services-v2-service/types';
import { ResourceSelection } from '@wix/ambassador-bookings-v2-booking/types';
import type { ListEligibleMembershipsResponse } from '@wix/ambassador-ecom-memberships-spi-host-v1-membership/types';
import type { CoveringPurchasableBenefit } from '../payment/benefitCoverage/benefitCoverage';
import {
  IntakeFormSubmission,
  Status,
} from '@wix/ambassador-intake-forms-v1-intake-form-submission/types';
import {
  ApprovalMode,
  PendingTriggerField,
} from '@wix/ambassador-intake-forms-v1-intake-form/types';
import { Member } from '@wix/ambassador-members-ng-api/types';
import { BookingsLineItemOption } from '@wix/bookings-checkout-api';
import { FormOverrides, FormValues } from '@wix/form-viewer';
import { GetActiveFeaturesResponse } from '../../types/ambassador/bookings/ambassador-services-catalog';
import { CouponInfo } from '../../types/coupons';
import { DynamicPriceInfo } from '../../types/dynamicPrice';
import { SelectedVariants } from '@wix/bookings-uou-types';
import { FormError } from '../../types/errors';
import { FormStatus } from '../../types/form-state';
import {
  FormNestedSlot,
  FormSelectedSlot,
} from '../../types/formSelectedSlots';
import type {
  BusinessInfo,
  CartModal,
  Dialog,
  PaymentMethod,
  PaymentOption,
  PlanInfoWithDescription,
  ServicePaymentDetails,
  SettingsSubTab,
  SettingsTab,
  SummaryPaymentDetails,
} from '../../types/types';
import { FormService } from '../mappers/serviceMapper/service.mapper';

export type EditorContext = {
  isDummy: boolean;
  selectedSettingsTabId?: SettingsTab;
  selectedSettingsSubTabId?: SettingsSubTab;
};

export type FormState = {
  eligiblePlans?: PlanInfoWithDescription[][];
  isPricingPlanInstalled: boolean;
  isMemberAreaInstalled: boolean;
  isUpsellPluginInstalled: boolean;
  isCart?: boolean;
  isServiceInCart?: boolean;
  couponInfo: CouponInfo;
  businessInfo: BusinessInfo;
  activeFeatures: GetActiveFeaturesResponse;
  memberDetails?: Member;
  // Authoritative login flag from `wixCodeApi.user.currentUser.loggedIn` (re-evaluated on controller
  // init + onUserLogin). Use this — not `memberDetails` — to gate on login state.
  isLoggedIn?: boolean;
  errors: FormError[];
  editorContext: EditorContext;
  status: FormStatus;
  dialog?: Dialog;
  isDayful?: boolean;
  wixForms: WixForms;
  selectedNumberOfParticipants: number;
  cartModal?: CartModal;
  bookingsLineItemOptions?: BookingsLineItemOption[];
  formSelectedSlot: FormSelectedSlot;
  serviceData: ServiceData;
  existingBookingDetails?: ExtendedBooking;
  isSMSAutomationsEnabled?: boolean;
  intakeForms?: IntakeForms;
  isSlotUnavailable?: boolean;
  selectedProductAddons: SelectedProduct[];
  relatedProducts: V3Product[];
  storesAppInstance?: string;
  isBookingForSomeoneElse?: boolean;
  /**
   * Whether this site's Pricing Plans are migrated to eCom, resolved once per form load in
   * `fetchInitialData`. Combine it with the `PlanAndBook` FT via `isPlanAndBookEnabled` —
   * never gate on the FT alone.
   */
  isPricingPlansMigratedToEcom?: boolean;
  expressCheckout?: ExpressCheckoutInfo;
};

export type ExpressCheckoutInfo = {
  mode?: ExpressCheckoutMode;
  buttonsState?: ExpressCheckoutButtonsState;
  numberOfButtons?: number;
  validationRequestId?: number;
};

export type WixForms = {
  initialValues: FormValues;
  overrides: FormOverrides;
  fieldProperties: WixFormsFieldProperties;
  // Per-participant values restored after browsing back from checkout, by participant id.
  participantInitialValues?: Record<string, FormValues>;
};

export interface WixFormsFieldProperties {
  [key: string]: {
    required: boolean;
    hidden: boolean;
    fieldType: string;
  };
}

export type IntakeForms = Record<string, IntakeForm>;

export type IntakeFormInfoMap = Record<string, IntakeFormInfo>;

export type IntakeForm = {
  formId: string;
  formName?: string;
  completionRequirement: CompletionRequirement;
  status: IntakeFormStatus;
  formData?: any;
  isLoading: boolean;
  previousSubmissionStatus?: Status;
  // Configured pending-approval triggers (form-level). Drives the anonymous login wall (Strategy A,
  // §8): a non-empty list on a form means an anonymous visitor must log in before filling it.
  pendingTriggerFields?: PendingTriggerField[];
  // `MANUAL_APPROVE_ALL` also drives the login wall (every submission pending, no triggers).
  approvalMode?: ApprovalMode;
};

export enum IntakeFormStatus {
  COMPLETED = 'completed',
  REQUIRED = 'required',
  OPTIONAL = 'optional',
  PENDING_APPROVAL = 'pending_approval',
}

export enum IntakeFormInfoType {
  VISITOR = 'visitorData',
  CONTACT = 'contactData',
}

export type ServiceData = {
  isSingleService: boolean;
  formId: string;
  summaryPaymentDetails: SummaryPaymentDetails;
  maxNumberOfParticipants: number;
  maxNumberOfParticipantsWithoutPP?: number;
  slotServices: SlotServices; // key is lineItemId
  allowBookForSomeoneElse: boolean;
};

export type IntakeFormInfo = {
  formName: string;
  mostRecentSubmission?: IntakeFormSubmission;
  // Form-level pending-approval triggers (populated on the visitor path from queryIntakeForm).
  pendingTriggerFields?: PendingTriggerField[];
  // Form-level approval mode (visitor path from queryIntakeForm).
  approvalMode?: ApprovalMode;
};

export type SlotServices = { [key: string]: SlotService }; // key is lineItemId

export type SlotService = {
  memberships?: ListEligibleMembershipsResponse;
  eligiblePlans?: PlanInfoWithDescription[];
  purchasableBenefits?: CoveringPurchasableBenefit[];
  selectedPaymentOption: PaymentOption;
  selectedPaymentType: PaymentMethod;
  service: FormService;
  nestedSlot: FormNestedSlot;
  paymentDetails: ServicePaymentDetails;
  dynamicPriceInfo?: DynamicPriceInfo;
  addOnByGroup?: SlotAddOnGroup[];
  selectedAddons?: SelectedAddons;
  // Multi-participant flow ("Add another participant"): one entry per participant, in
  // display order; index 0 is the booker. Undefined outside the flow.
  participants?: Participant[];
  resourceSelections?: ResourceSelection[];
};

export type ParticipantPayment = {
  selectedPaymentOption: PaymentOption;
  selectedPaymentType: PaymentMethod;
};

export type Participant = {
  // Stable identity (the widget keys form values / DOM refs by it).
  id: string;
  selectedAddons: SelectedAddons;
  // Single-choice price variant (numberOfParticipants: 1) when the service has variants.
  selectedVariant?: SelectedVariants;
  // Own payment selection for participants after the first. Undefined for the booker
  // (who pays with the slot's selectedPaymentOption/Type) or while the default is pending.
  payment?: ParticipantPayment;
};

export interface SelectedAddonInfo {
  id: string;
  quantity: number;
  durationInMinutes?: number;
  price?: string | null;
  currency?: string | null;
}

export interface SelectedAddons {
  [groupId: string]: {
    [addonId: string]: SelectedAddonInfo;
  };
}

export type SlotAddOnGroup = {
  id?: string | null;
  name?: string | null;
  prompt?: string | null;
  maxNumberOfAddOns?: number | null;
  addOns?: SlotAddOn[] | null;
};

export type SlotAddOnImage = {
  /** WixMedia image ID, e.g. "4b39...9702.jpg". Preferred over `url`. */
  id?: string;
  url?: string;
  width?: number;
  height?: number;
  altText?: string | null;
};

export type SlotAddOn = {
  id?: string | null;
  name?: string | null;
  duration?: number | null;
  price?: string | null;
  currency?: string | null;
  maxQuantity?: number | null;
  image?: SlotAddOnImage | null;
};

export type ServiceAddOnGroup = {
  id?: string;
  name?: string;
  addOnIds?: string[];
};
